import type { AccountDetails, InvoiceTag, TaxCode } from "@/apis/gql/types";
import { SpecialFilterValue } from "@/components/shared/invoices-filters/filter/utils";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { CreatedAtCell } from "@/features/invoices-table/cells/created-at";
import { DescriptionCell } from "@/features/invoices-table/cells/description";
import { SupplierNameCell } from "@/features/invoices-table/cells/supplier";
import { TagsCell } from "@/features/invoices-table/cells/tags";
import { TotalAmountCell } from "@/features/invoices-table/cells/total-amount";
import { usePermissions } from "@/hooks/usePermissions";
import { Tags } from "@/lib/miscellaneous/tags";
import { useTenant } from "@/providers/session";
import type { CellContext, ColumnDef } from "@tanstack/react-table";

import type { InvoiceRowData } from "@/components/shared/data-table";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { DatevBuCell } from "./cells/datev-bu";
import { ExpenseAccountCell } from "./cells/expense-account";
import { InvoiceDateCell } from "./cells/invoice-date";
import { InvoiceNumberCell } from "./cells/invoice-number";
import { SelectCell } from "./cells/select";
import { StartDateCell } from "./cells/start-date";
import { StatusCell } from "./cells/status";
import { InvoiceTypeCell } from "./cells/type";
import { CreatedAtHeader } from "./headers/created-at";
import { DatevBuHeader } from "./headers/datev-bu";
import { DescriptionHeader } from "./headers/description";
import { ExpenseAccountHeader } from "./headers/expense-account";
import { InvoiceDateHeader } from "./headers/invoice-date";
import { InvoiceNumberHeader } from "./headers/invoice-number";
import { StartDateHeader } from "./headers/start-date";
import { StatusHeader } from "./headers/status";
import { SupplierNameHeader } from "./headers/supplier-name";
import { TagsHeader } from "./headers/tags";
import { TotalAmountHeader } from "./headers/total-amount";
import { InvoiceTypeHeader } from "./headers/type";

const selectColumn: ColumnDef<InvoiceRowData> = {
  id: "select",
  size: 42,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => <SelectCell {...cell} />,
  enableSorting: false,
  enableHiding: false,
};

const supplierNameColumn: (
  showConfidence: boolean,
  invoiceType?: InvoiceTypeString,
) => ColumnDef<InvoiceRowData> = (showConfidence, invoiceType) => ({
  accessorKey: "supplier.supplierName",
  id: "supplierName",
  size: 250,
  filterFn: (row, columnId, value) => {
    return row.original?.supplier?.supplierId === value;
  },
  header: (header) => (
    <SupplierNameHeader {...header} invoiceType={invoiceType} />
  ),
  cell: (cell) => (
    <SupplierNameCell {...cell} showConfidence={showConfidence} />
  ),
});

const totalAmountColumn: (
  showConfidence: boolean,
) => ColumnDef<InvoiceRowData> = (showConfidence) => ({
  accessorKey: "invoice.totalAmount",
  id: "totalAmount",
  size: 100,
  header: (header) => <TotalAmountHeader {...header} />,
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;

    if (!cell.row.original.invoice.isBooking) {
      return null;
    }

    return (
      <TotalAmountCell
        invoice={cell.row.original.invoice}
        showConfidence={showConfidence}
      />
    );
  },
});

const descriptionColumn: ColumnDef<InvoiceRowData> = {
  id: "description",
  accessorKey: "invoice.aiSummary",
  header: (columns) => <DescriptionHeader {...columns} />,
  enableSorting: false,
  sortingFn: (a, b) => {
    const descriptionA = a.original.invoice.aiSummary ?? "";
    const descriptionB = b.original.invoice.aiSummary ?? "";

    if (descriptionA === "" && descriptionB === "") {
      return 0;
    } else if (descriptionA === "") {
      return 1;
    } else if (descriptionB === "") {
      return -1;
    } else {
      return descriptionA.localeCompare(descriptionB);
    }
  },

  cell: (cell) => <DescriptionCell {...cell} />,
};

const invoiceNoColumn: (showConfidence: boolean) => ColumnDef<InvoiceRowData> =
  (showConfidence) => ({
    accessorKey: "invoice.invoiceNo",
    id: "invoiceNo",
    header: (header) => <InvoiceNumberHeader {...header} />,
    cell: (cell) => {
      const { isSuperAdmin } = usePermissions();
      const { isOcrReviewRequired } = useTenant();

      if (
        isOcrReviewRequired &&
        !cell.row.original.invoice.ocrReviewed &&
        !isSuperAdmin
      )
        return null;
      return <InvoiceNumberCell {...cell} showConfidence={showConfidence} />;
    },
  });

const invoiceDateColumn: (
  showConfidence: boolean,
) => ColumnDef<InvoiceRowData> = (showConfidence) => ({
  id: "invoiceDate",
  accessorKey: "invoice.invoiceDate",
  size: 120,
  header: (header) => <InvoiceDateHeader {...header} />,
  sortingFn: (a, b) => {
    const invoiceDateA = a.original?.invoice.invoiceDate;
    const invoiceDateB = b.original?.invoice.invoiceDate;
    // Convert date strings to Date objects, using a large date for undefined/null
    const dateA = invoiceDateA
      ? new Date(invoiceDateA)
      : new Date(8640000000000000);
    const dateB = invoiceDateB
      ? new Date(invoiceDateB)
      : new Date(8640000000000000);

    // Compare the dates
    return dateA.getTime() - dateB.getTime();
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return (
      <InvoiceDateCell
        invoice={cell.row.original.invoice}
        showConfidence={showConfidence}
      />
    );
  },
});

const startDateColumn: (showConfidence: boolean) => ColumnDef<InvoiceRowData> =
  (showConfidence) => ({
    id: "serviceDate",
    accessorKey: "invoice.serviceDate",
    size: 120,
    header: (header) => <StartDateHeader {...header} />,
    sortingFn: (a, b) => {
      const startDateA = a.original?.invoice.serviceDate;
      const startDateB = b.original?.invoice.serviceDate;
      // Convert date strings to Date objects, using a large date for undefined/null
      const dateA = startDateA
        ? new Date(startDateA)
        : new Date(8640000000000000);
      const dateB = startDateB
        ? new Date(startDateB)
        : new Date(8640000000000000);

      // Compare the dates
      return dateA.getTime() - dateB.getTime();
    },
    cell: (cell) => {
      const { isSuperAdmin } = usePermissions();
      const { isOcrReviewRequired } = useTenant();

      if (
        isOcrReviewRequired &&
        !cell.row.original.invoice.ocrReviewed &&
        !isSuperAdmin
      )
        return null;

      return <StartDateCell {...cell} showConfidence={showConfidence} />;
    },
  });

const expenseAccountColumn: (
  showConfidence: boolean,
  accounts: AccountDetails[],
  readOnly: boolean,
) => ColumnDef<InvoiceRowData> = (showConfidence, accounts, readOnly) => ({
  size: 200,
  id: "expenseAccount",
  accessorKey: "expenseAccount",
  filterFn: (row, columnId, value) => {
    // TODO: I think this is unused because of server side filtering
    if (value === SpecialFilterValue.Unknown) {
      // Return true if any of the line items do not have an expense account ID
      return row.original.lineItems.some((lineItem) => !lineItem.accountId);
    } else {
      // Return true if any line item has an expense account ID that matches the filter value
      return row.original.invoice.accountIds.some(
        (accountId) => accountId === value,
      );
    }
  },
  header: (header) => <ExpenseAccountHeader {...header} />,
  sortingFn: (a, b) => {
    const aId = a.original?.invoice.accountIds?.[0] as string;
    const bId = b.original?.invoice.accountIds?.[0] as string;

    const aAccountName =
      accounts.find((account) => account.accountId === aId)?.accountName ?? "";
    const bAccountName =
      accounts.find((account) => account.accountId === bId)?.accountName ?? "";

    if (aAccountName === "" && bAccountName === "") {
      return 0;
    } else if (aAccountName === "") {
      return 1;
    } else if (bAccountName === "") {
      return -1;
    } else {
      return aAccountName.localeCompare(bAccountName);
    }
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return (
      <ExpenseAccountCell
        {...cell}
        showConfidence={showConfidence}
        readOnly={readOnly}
      />
    );
  },
});

const datevBuColumn: (
  showConfidence: boolean,
  taxCodes: TaxCode[],
  readOnly: boolean,
) => ColumnDef<InvoiceRowData> = (showConfidence, taxCodes, readOnly) => ({
  id: "datevBu",
  size: 200,
  accessorKey: "datevBu",
  header: (header) => <DatevBuHeader {...header} />,
  sortingFn: (a, b) => {
    const aId = a.original?.invoice.taxCodeIds?.[0] as string;
    const bId = b.original?.invoice.taxCodeIds?.[0] as string;

    const aTaxCodeName =
      taxCodes.find((taxCode) => taxCode.taxCodeId === aId)?.datevBu ?? "";
    const bTaxCodeName =
      taxCodes.find((taxCode) => taxCode.taxCodeId === bId)?.datevBu ?? "";

    if (aTaxCodeName === "" && bTaxCodeName === "") {
      return 0;
    } else if (aTaxCodeName === "") {
      return 1;
    } else if (bTaxCodeName === "") {
      return -1;
    } else {
      return aTaxCodeName.localeCompare(bTaxCodeName);
    }
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return (
      <DatevBuCell
        {...cell}
        showConfidence={showConfidence}
        readOnly={readOnly}
      />
    );
  },
});

const tagsColumn: ColumnDef<InvoiceRowData> = {
  accessorKey: "tags",
  id: "tags",
  size: 120,
  header: (header) => <TagsHeader {...header} />,
  filterFn: (row, columnId, value) => {
    const duplicate = row.original.invoice.isDuplicate;

    if (value?.includes(Tags.NoTags)) {
      if (!duplicate) {
        return row.original.tags?.length === 0;
      }
      return false;
    }

    if (value?.includes(Tags.DuplicateInvoice) && duplicate) {
      return true;
    }

    const rowTags = row.original.tags ?? [];

    const tagNames = rowTags?.map((tag) => tag.tagName);

    if (tagNames.length === 0) return false;

    return value.some((tag: string) => tagNames.includes(tag));
  },
  sortingFn: (a, b) => {
    let tagsA = a.original.tags?.map((tag) => tag.tagName).sort() ?? [];
    let tagsB = b.original.tags?.map((tag) => tag.tagName).sort() ?? [];

    const isDuplicateA = a.original.invoice.isDuplicate;
    const isDuplicateB = b.original.invoice.isDuplicate;

    if (isDuplicateA) {
      tagsA.push(Tags.DuplicateInvoice);
    }

    if (isDuplicateB) {
      tagsB.push(Tags.DuplicateInvoice);
    }

    tagsA = tagsA.sort();
    tagsB = tagsB.sort();

    // Compare the tags lexicographically
    for (let i = 0; i < Math.max(tagsA.length, tagsB.length); i++) {
      if (tagsA[i] === undefined) return 1;
      if (tagsB[i] === undefined) return -1;
      const comparison = tagsA[i].localeCompare(tagsB[i]);
      if (comparison !== 0) return comparison;
    }

    return 0;
  },
  cell: (cell) => {
    const { isSuperAdmin } = usePermissions();
    const { isOcrReviewRequired } = useTenant();

    if (
      isOcrReviewRequired &&
      !cell.row.original.invoice.ocrReviewed &&
      !isSuperAdmin
    )
      return null;
    return <TagsCell {...cell} />;
  },
};

const createdAtColumn: ColumnDef<InvoiceRowData> = {
  id: "createdAt",
  accessorKey: "invoice.createdAt",
  header: (header) => <CreatedAtHeader {...header} />,
  cell: (cell) => (
    <CreatedAtCell createdAt={cell.row.original.invoice.createdAt} />
  ),
};

const statusColumn: ColumnDef<InvoiceRowData> = {
  accessorKey: "invoice.status",
  id: "status",
  size: 175,
  header: (header) => <StatusHeader {...header} />,
  filterFn: (row, columnId, value) => {
    if (!value || value.length === 0) return true;

    const status = row.original?.invoice.status;

    return value.includes(status);
  },
  meta: {
    getCellContext: (context: CellContext<InvoiceRowData, any>) => {
      return {
        align: "center",
        filterVariant: "select",
      };
    },
  },
  cell: (cell) => (
    <div className="flex items-center justify-start">
      <StatusCell {...cell} />
    </div>
  ),
};

const reviewTypeColumn: ColumnDef<InvoiceRowData> = {
  id: "reviewType",
  accessorKey: "invoice.reviewType",
  filterFn: (row, columnId, value) => {
    if (!value || value.length === 0) return true;

    const reviewType = row.original?.invoice.reviewType;

    return value.includes(reviewType);
  },
};

const ocrReviewed: ColumnDef<InvoiceRowData> = {
  id: "ocrReviewed",
  accessorKey: "invoice.ocrReviewed",
};

const isBooking: ColumnDef<InvoiceRowData> = {
  id: "isBooking",
  accessorKey: "invoice.isBooking",
};

const isDuplicate: ColumnDef<InvoiceRowData> = {
  id: "isDuplicate",
  accessorKey: "invoice.isDuplicate",
};

const invoiceTypeColumn: ColumnDef<InvoiceRowData> = {
  id: "type",
  accessorKey: "invoice.type",
  size: 120,
  header: (header) => <InvoiceTypeHeader {...header} />,
  cell: (cell) => <InvoiceTypeCell {...cell} />,
};

export const columns: (
  showConfidences: boolean,
  taxCodes: TaxCode[],
  accounts: AccountDetails[],
  readOnly: boolean,
  invoiceType?: InvoiceTypeString,
) => ColumnDef<InvoiceRowData>[] = (
  showConfidences,
  taxCodes,
  accounts,
  readOnly,
  invoiceType,
) => [
  selectColumn,
  invoiceTypeColumn,
  statusColumn,
  tagsColumn,
  supplierNameColumn(showConfidences, invoiceType),
  totalAmountColumn(showConfidences),
  descriptionColumn,
  invoiceNoColumn(showConfidences),
  invoiceDateColumn(showConfidences),
  startDateColumn(showConfidences),
  datevBuColumn(showConfidences, taxCodes, readOnly),
  expenseAccountColumn(showConfidences, accounts, readOnly),
  createdAtColumn,
  reviewTypeColumn,
  ocrReviewed,
  isBooking,
  isDuplicate,
];
